/**
 * Proxy-aware JSON fetch helpers. Inside Discord all requests are routed through `/.proxy`;
 * locally Vite proxies `/api` to the Express server. Every caller uses relative paths.
 * @see docs/01-activity-build-guide.md §4
 */
import type {
  DraftResponse, NewPlaceResponse, Place, PlacesSearchResponse, ProfileResponse,
  ReviewPayload, ReviewResponse, SubmitMode,
} from '@pghbites/shared';
import { apiBase } from './sdk/env.js';
import { authHeader } from './auth.js';

/** JSON headers for a write/cost request: content-type + the session bearer token. */
function writeHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeader() };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function postJson<TReq extends object, TRes>(path: string, body: TReq): Promise<TRes> {
  const res = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = (data as { error?: string })?.error ?? `Request to ${path} failed (${res.status}).`;
    throw new ApiError(message, res.status);
  }
  return data as TRes;
}

/**
 * Submit a review. Returns the ReviewResponse for any HTTP status (the body shape is consistent:
 * ok/status/message/errors), so the caller inspects `ok` rather than catching. Throws only on a
 * network failure.
 */
export async function submitReview(mode: SubmitMode, review: ReviewPayload): Promise<ReviewResponse> {
  const res = await fetch(`${apiBase()}/api/review`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ mode, review }),
  });
  const data = (await res.json().catch(() => null)) as ReviewResponse | null;
  if (!data) {
    return { ok: false, status: 'stored_only', message: 'Could not reach the server. Please try again.' };
  }
  return data;
}

/** Compose-only: get an AI draft to review/refine. No posting happens server-side. */
export async function composeDraft(review: ReviewPayload): Promise<DraftResponse> {
  const res = await fetch(`${apiBase()}/api/review/draft`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ review }),
  });
  const data = (await res.json().catch(() => null)) as DraftResponse | null;
  if (!data) return { ok: false, message: 'Could not reach the server. Please try again.' };
  return data;
}

/** Post the member-approved (possibly edited) draft. Server re-checks the output guardrails. */
export async function confirmReview(review: ReviewPayload, draft: string): Promise<ReviewResponse> {
  const res = await fetch(`${apiBase()}/api/review/confirm`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ review, draft }),
  });
  const data = (await res.json().catch(() => null)) as ReviewResponse | null;
  if (!data) return { ok: false, status: 'stored_only', message: 'Could not reach the server. Please try again.' };
  return data;
}

/**
 * Read the member's saved review voice preset (member_preferences.review_voice) so the Activity
 * can default to it on launch. Returns '' if the member has none set or the call fails — the form
 * then keeps its built-in default (Yinzer). Best-effort: never throws.
 */
export async function fetchSavedVoice(): Promise<string> {
  try {
    const res = await fetch(`${apiBase()}/api/profile`, { headers: { ...authHeader() } });
    if (!res.ok) return '';
    const data = (await res.json().catch(() => null)) as ProfileResponse | null;
    return data?.preferences?.review_voice ?? '';
  } catch {
    return '';
  }
}

/** Search the places source for the location autocomplete. Returns [] on any failure. */
export async function searchPlaces(q: string): Promise<Place[]> {
  try {
    const res = await fetch(`${apiBase()}/api/places?q=${encodeURIComponent(q)}`);
    const data = (await res.json().catch(() => null)) as PlacesSearchResponse | null;
    return data?.places ?? [];
  } catch {
    return [];
  }
}

/** Add a new spot. Without confirm, the server may return a "did you mean" suggestion. */
export async function addNewPlace(name: string, neighborhood?: string, confirm?: boolean): Promise<NewPlaceResponse> {
  try {
    const res = await fetch(`${apiBase()}/api/places/new`, {
      method: 'POST',
      headers: writeHeaders(),
      body: JSON.stringify({ name, neighborhood, confirm }),
    });
    const data = (await res.json().catch(() => null)) as NewPlaceResponse | null;
    return data ?? { ok: false, message: 'Could not reach the server. Please try again.' };
  } catch {
    return { ok: false, message: 'Could not reach the server. Please try again.' };
  }
}
